import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { ApiError, type OperationKey, type OpOutput } from '@estudio/contracts';
import { NetworkError, type OpInputLoose } from '@estudio/cloud-client';
import { useServices } from './services.tsx';

export function opKey<K extends OperationKey>(key: K, input: OpInputLoose<K>) {
  return ['op', key, input] as const;
}

/** Lectura tipada del contrato. El servidor es la autoridad. */
export function useOp<K extends OperationKey>(
  key: K,
  input: OpInputLoose<K> | null,
  opts: Partial<UseQueryOptions<OpOutput<K>, Error>> = {},
) {
  const { client } = useServices();
  return useQuery<OpOutput<K>, Error>({
    queryKey: ['op', key, input],
    queryFn: ({ signal }) => client.call(key, input as OpInputLoose<K>, { signal }),
    enabled: input !== null && (opts.enabled ?? true),
    ...opts,
  });
}

/** Escritura tipada; al confirmar, invalida lecturas para reflejar el estado del servidor. */
export function useMutationOp<K extends OperationKey>(key: K, opts: { onSuccess?: (data: OpOutput<K>) => void; invalidate?: boolean } = {}) {
  const { client } = useServices();
  const qc = useQueryClient();
  return useMutation<OpOutput<K>, Error, OpInputLoose<K>>({
    mutationFn: (input) => client.call(key, input),
    onSuccess: async (data) => {
      if (opts.invalidate !== false) await qc.invalidateQueries({ queryKey: ['op'] });
      opts.onSuccess?.(data);
    },
  });
}

const MESSAGES: Partial<Record<string, string>> = {
  AUTH_REQUIRED: 'Tu sesión venció. Vuelve a entrar.',
  FORBIDDEN: 'No tienes permiso para esta acción.',
  NOT_FOUND: 'No se encontró el recurso (o no pertenece a tu espacio).',
  QUOTA_EXCEEDED: 'Se alcanzó la cuota del espacio.',
  DEVICE_REQUIRED: 'Requiere la aplicación de escritorio.',
  CAPABILITY_UNAVAILABLE: 'Esta capacidad no está disponible todavía.',
  PROVIDER_ACTION_REQUIRED: 'Falta una acción en el proveedor o destino.',
  DOCUMENT_DELETED: 'El documento fue eliminado en otro equipo.',
  CURSOR_EXPIRED: 'La sincronización se reinició desde una instantánea.',
};

export function describeError(e: unknown): string {
  if (e instanceof NetworkError) return 'Sin conexión con el servidor. Tus cambios quedan pendientes en este equipo.';
  if (e instanceof ApiError) {
    const base = e.message && e.message !== e.code ? e.message : (MESSAGES[e.code] ?? e.code);
    const missing = e.details?.missing as string[] | undefined;
    const issues = e.details?.issues as { path?: string; message: string }[] | undefined;
    const extra = missing?.length ? ` Falta: ${missing.join(', ')}.` : issues?.length ? ` ${issues.slice(0, 3).map((i) => (i.path ? `${i.path}: ` : '') + i.message).join('; ')}` : '';
    return base + extra;
  }
  return (e as Error)?.message ?? 'Error desconocido';
}

export function isCode(e: unknown, code: string): boolean {
  return e instanceof ApiError && e.code === code;
}
